/**
 * Kick rotation (PLAN §7). Every bot computes the same assignment from the shared
 * world, so no bot-to-bot messaging is needed:
 *   - kickers = party members with an interrupt (tank + both DPS), in slot order,
 *     rotated by the pull counter so the first kick moves around between pulls;
 *   - each interruptible enemy cast gets the first eligible kicker (ready, in range)
 *     as primary and the next one as backup;
 *   - the primary kicks at a random 30–70% of the cast, the backup at ~80% if it is
 *     still going. Each kick passes a kickReliability roll.
 * Human kickers take part in the order; bots then only cover them as backup.
 * When nobody is in range, a ready bot kicker that can walk into range before the cast
 * ends is assigned and steps toward the caster (melee kickers vs ranged casters).
 * +encounters: shield-gated casts (`interruptibleWhen`, e.g. Feeding Frenzy behind Gorged Hide)
 * with kickPriority <= GATED_RESERVE_PRIORITY get their kickers reserved while the cast is still
 * grey, so nobody spends the kick on filler. Once the shield breaks the primary kicks after its
 * reaction time (not at 30–70% of a long channel); the backup follows GATED_BACKUP_MS later.
 */
import type { AbilityDef, Command, EnemyAbility, UnitId } from '@keystone/sim';
import { dist } from '../geometry';
import type { ClassKit } from '../kits';
import { noticed, reactionMsFor, rollFor, type BotMemory } from '../memory';
import { abilityLocked, cooldownReady, inAbilityRange, meleeRange, type Perception, type RUnit } from '../perception';

export interface KickAssignment {
  casterId: UnitId;
  primary: UnitId | null;
  backup: UnitId | null;
}

export type KitLookup = (u: RUnit) => ClassKit | undefined;

const KIND_PRIORITY: Partial<Record<EnemyAbility['kind'], number>> = { heal: 0, shield: 0, cc: 1, bolt: 2 };
/** +encounters: kind rank → kickPriority scale (heal/shield 1.5, cc 4.5, bolt 6.5, other 7). */
const KIND_FALLBACK = [1.5, 4.5, 6.5, 7];

/** Interrupt order for the current pull. */
export function kickOrder(p: Perception, kitOf: KitLookup): UnitId[] {
  const kickers = p.partyAll.filter((u) => {
    const id = kitOf(u)?.interrupt;
    return !!id && !!p.view.content.ability(id);
  });
  if (kickers.length === 0) return [];
  const shift = p.world.mplus.pulls % kickers.length;
  return [...kickers.slice(shift), ...kickers.slice(0, shift)].map((u) => u.id);
}

/** Walking speed assumed for reachability (WoW run speed). */
const RUN_SPEED = 7;
/** Time kept in hand after arriving (face + cast + reaction slack). */
const KICK_SLACK_MS = 600;

/** Yards the kicker must close before its interrupt reaches the caster (0 = in range). */
function gapTo(p: Perception, k: RUnit, caster: RUnit, ab: AbilityDef): number {
  if (inAbilityRange(k, caster, ab)) return 0;
  const reach = ab.melee || (!ab.range && ab.targeting === 'enemy') ? meleeRange(k, caster) : (ab.range ?? 0) + k.combatReach + caster.combatReach;
  return Math.max(0, dist(k.pos, caster.pos) - reach);
}

/**
 * The interrupt a kicker would use now: its kick when ready, else (F13) its secondary interrupt
 * (Warden Aegis Throw) when ready and off the GCD. Undefined when neither is available.
 */
export function readyInterrupt(p: Perception, kitOf: KitLookup, k: RUnit): AbilityDef | undefined {
  const kit = kitOf(k);
  const main = p.view.content.ability(kit?.interrupt ?? '');
  if (main && cooldownReady(k, main) && !abilityLocked(p.view, k, main.id)) return main;
  const alt = p.view.content.ability(kit?.secondaryInterrupt ?? '');
  if (alt && cooldownReady(k, alt) && !abilityLocked(p.view, k, alt.id) && (alt.gcdMs === 0 || k.gcdUntil <= p.now) && !k.cast) return alt;
  return undefined;
}

function canKick(p: Perception, kitOf: KitLookup, kickerId: UnitId, caster: RUnit, reachable = false): boolean {
  const k = p.world.units[kickerId];
  if (!k?.alive) return false;
  const ab = readyInterrupt(p, kitOf, k);
  if (!ab) return false;
  const gap = gapTo(p, k, caster, ab);
  if (gap === 0) return true;
  if (!reachable || k.controller !== 'bot' || !caster.cast) return false;
  const left = caster.cast.endsAt - p.now - KICK_SLACK_MS;
  return left > 0 && (gap / RUN_SPEED) * 1000 <= left;
}

/** +encounters: gated casts at or above this priority (1 = top) reserve their kickers. */
export const GATED_RESERVE_PRIORITY = 2;
/** +encounters: backup kick delay after the primary's planned kick on a gated cast. */
const GATED_BACKUP_MS = 700;

/** +encounters: kick priority of a running cast (1 = first). */
export function castKickPriority(p: Perception, e: RUnit): number {
  const ab = p.view.content.enemyAbility(e.cast?.abilityId ?? '');
  return ab?.kickPriority ?? KIND_FALLBACK[KIND_PRIORITY[ab?.kind ?? 'bolt'] ?? 3] ?? 7;
}

/** +encounters: is this a shield-gated cast (its interruptibility depends on a caster aura)? */
export function isGatedCast(p: Perception, e: RUnit): boolean {
  return !!e.cast && !!p.view.content.enemyAbility(e.cast.abilityId)?.interruptibleWhen;
}

/** +encounters: a high-priority gated cast still behind its shield (kick reserved, burst the shield). */
export function shieldedCast(p: Perception, e: RUnit): boolean {
  if (!e.cast || e.cast.interruptible || e.cast.endsAt <= p.now) return false;
  const ab = p.view.content.enemyAbility(e.cast.abilityId);
  return !!ab?.interruptible && !!ab.interruptibleWhen && castKickPriority(p, e) <= GATED_RESERVE_PRIORITY;
}

/**
 * Interruptible enemy casts in kick-priority order, soonest-ending first within a priority.
 * +encounters: high-priority shield-gated casts are included while still shielded (reservations).
 */
export function kickableCasts(p: Perception): RUnit[] {
  const casts = p.engaged.filter((e) => e.cast && e.cast.endsAt > p.now && (e.cast.interruptible || shieldedCast(p, e)));
  // +encounters: explicit kickPriority (1 = first) beats the kind-based default.
  const prio = (e: RUnit): number => castKickPriority(p, e);
  return casts.sort((a, b) => prio(a) - prio(b) || (a.cast?.endsAt ?? 0) - (b.cast?.endsAt ?? 0));
}

export function assignKicks(p: Perception, kitOf: KitLookup): KickAssignment[] {
  const order = kickOrder(p, kitOf);
  const casts = kickableCasts(p);
  const used = new Set<UnitId>();
  const pick = (c: RUnit): UnitId | null => {
    const id = order.find((k) => !used.has(k) && canKick(p, kitOf, k, c)) ?? order.find((k) => !used.has(k) && canKick(p, kitOf, k, c, true)) ?? null;
    if (id) used.add(id);
    return id;
  };
  const out: KickAssignment[] = casts.map((c) => ({ casterId: c.id, primary: pick(c), backup: null }));
  for (const a of out) a.backup = pick(p.world.units[a.casterId] as RUnit);
  return out;
}

/** Casts at or below this priority (larger number = less important) are filler. */
export const FILLER_KICK_PRIORITY = 6;
/** A filler cast on an ally below this HP is kicked anyway. */
const FILLER_KICK_LOW_HP = 0.4;

/**
 * Should a disciplined kicker skip this (filler) cast? Yes while any engaged enemy (the caster
 * included) has an interruptible ability with a better kick priority in its kit: that cast will
 * come, and the kick must be ready for it. A filler aimed at a low ally is still kicked.
 */
export function saveKickFor(p: Perception, caster: RUnit): boolean {
  if (!caster.cast) return false;
  const prio = castKickPriority(p, caster);
  if (prio < FILLER_KICK_PRIORITY) return false;
  const tgt = caster.cast.targetId ? p.world.units[caster.cast.targetId] : undefined;
  if (tgt && tgt.faction !== caster.faction && tgt.maxHp > 0 && tgt.hp / tgt.maxHp < FILLER_KICK_LOW_HP) return false;
  for (const e of p.engaged) {
    if (!e.alive || !e.npcId) continue;
    const def = p.view.content.npc(e.npcId);
    if (!def) continue;
    for (const id of def.abilities) {
      const ab = p.view.content.enemyAbility(id);
      if (!ab?.interruptible) continue;
      const k = ab.kickPriority ?? KIND_FALLBACK[KIND_PRIORITY[ab.kind] ?? 3] ?? 7;
      if (k < FILLER_KICK_PRIORITY) return true;
    }
  }
  return false;
}

export function planKick(p: Perception, kit: ClassKit, kitOf: KitLookup, mem: BotMemory): Command[] | null {
  if (!kit.interrupt) return null;
  // +field: a hero below the kick's unlock level has no kick (unless the secondary one is open).
  if (abilityLocked(p.view, p.self, kit.interrupt) && (!kit.secondaryInterrupt || abilityLocked(p.view, p.self, kit.secondaryInterrupt))) return null;
  const self = p.self;
  for (const a of assignKicks(p, kitOf)) {
    const role = a.primary === self.id ? 'primary' : a.backup === self.id ? 'backup' : null;
    if (!role) continue;
    const caster = p.world.units[a.casterId];
    const cast = caster?.cast;
    if (!caster || !cast) continue;
    const kickDef = readyInterrupt(p, kitOf, self) ?? p.view.content.ability(abilityLocked(p.view, self, kit.interrupt) ? (kit.secondaryInterrupt ?? kit.interrupt) : kit.interrupt);
    if (kickDef && !inAbilityRange(self, caster, kickDef)) {
      // Assigned but out of range: step in once the cast is noticed (never a human).
      if (self.controller !== 'bot' || p.now < cast.startedAt + reactionMsFor(mem, `kick:${caster.id}:${cast.startedAt}`)) continue;
      if (mem.skill.kickDiscipline && saveKickFor(p, caster)) continue;
      if (!p.view.queries.controlState(self.id).canMove) continue;
      if (self.move?.mode === 'dest' && dist(self.move.dest, caster.pos) < 1.5) return [];
      mem.lastMoveDest = { x: caster.pos.x, y: caster.pos.y };
      const cmds: Command[] = [];
      if (self.cast) cmds.push({ type: 'cancelCast', actorId: self.id });
      cmds.push({ type: 'move', actorId: self.id, dest: { x: caster.pos.x, y: caster.pos.y } });
      return cmds;
    }
    // Discipline (skill dial): don't burn the kick on a filler cast while the pull holds a caster
    // with something that matters more; the kick is better saved for it.
    if (mem.skill.kickDiscipline && saveKickFor(p, caster)) continue;
    const dur = Math.max(1, cast.endsAt - cast.startedAt);
    let perceived = cast.startedAt + reactionMsFor(mem, `kick:${caster.id}:${cast.startedAt}`);
    const key = `kick:${role}:${caster.id}:${cast.startedAt}`;
    // Undisciplined kickers wait longer into the cast bar before reacting with the kick.
    const [lo, hi] = mem.skill.kickDiscipline ? [0.3, 0.7] : [0.45, 0.9];
    const roll = rollFor(mem, key, mem.skill.kickReliability, lo, hi);
    let planned = role === 'primary' ? cast.startedAt + roll.at * dur : Math.min(cast.startedAt + 0.8 * dur, cast.endsAt - 350);
    if (isGatedCast(p, caster) && castKickPriority(p, caster) <= GATED_RESERVE_PRIORITY) {
      // +encounters: reserved while shielded (hold the kick, keep bursting); kick soon after it breaks.
      const gateKey = `gate:${caster.id}:${cast.startedAt}`;
      if (!noticed(mem, gateKey, cast.interruptible, p.now)) continue;
      const opened = mem.noticed.get(gateKey)!.first;
      perceived = opened + reactionMsFor(mem, `gate:${caster.id}:${cast.startedAt}`);
      planned = role === 'primary' ? perceived + roll.at * 400 : perceived + GATED_BACKUP_MS + roll.at * 400;
    }
    if (!roll.ok || p.now < Math.max(perceived, planned) || p.now >= cast.endsAt - 50) continue;
    if (!p.view.queries.controlState(self.id).canAct) return null;
    const cmds: Command[] = [];
    if (self.cast) cmds.push({ type: 'cancelCast', actorId: self.id });
    cmds.push({ type: 'face', actorId: self.id, targetId: caster.id });
    cmds.push({ type: 'cast', actorId: self.id, abilityId: kickDef?.id ?? kit.interrupt, targetId: caster.id });
    return cmds;
  }
  return null;
}
